/**
 * Área de input - Versão Premium
 * Com animações, upload de arquivos, gravação de áudio (MediaRecorder) e melhor UX
 * Fase 5.4 - Suporte a upload de arquivos
 * Fase 5.5 - Suporte a captura/envio de áudio pelo microfone do visitante
 */

import React, { useState, useRef, useEffect } from 'react';

// Tipos de arquivo permitidos (extendido para aceitar áudio capturado/anexado)
const ALLOWED_FILE_TYPES = {
  image: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  document: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
  audio: ['audio/webm', 'audio/mp4', 'audio/ogg', 'audio/mpeg', 'audio/wav', 'audio/x-m4a', 'audio/aac'],
};
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_RECORDING_SECONDS = 5 * 60; // 5 minutos – limite duro para evitar uploads pesados

interface InputAreaProps {
  onSend: (message: string) => void;
  onSendFile?: (file: File) => void;
  primaryColor: string;
  disabled?: boolean;
  placeholder?: string;
  onTypingStart?: () => void;
  onTypingStop?: () => void;
  enableFileUpload?: boolean;
}

/**
 * Detecta se o navegador suporta gravação de áudio.
 * Necessário pois Safari < 14, navegadores antigos e iframes sem permissão
 * não expõem MediaRecorder ou navigator.mediaDevices.
 */
const isAudioRecordingSupported = (): boolean => {
  if (typeof window === 'undefined') return false;
  if (!('MediaRecorder' in window)) return false;
  if (!navigator?.mediaDevices?.getUserMedia) return false;
  return true;
};

/**
 * Escolhe o melhor MIME type suportado pelo MediaRecorder no navegador atual.
 * Ordem de prioridade alinhada com a compatibilidade do backend (extensionMap).
 */
const pickBestAudioMime = (): { mime: string; ext: string } => {
  const candidates: Array<{ mime: string; ext: string }> = [
    { mime: 'audio/webm;codecs=opus', ext: 'webm' },
    { mime: 'audio/webm', ext: 'webm' },
    { mime: 'audio/mp4', ext: 'm4a' },
    { mime: 'audio/ogg;codecs=opus', ext: 'ogg' },
    { mime: 'audio/ogg', ext: 'ogg' },
  ];
  for (const c of candidates) {
    try {
      const MR = (typeof MediaRecorder !== 'undefined' ? MediaRecorder : null) as
        | (typeof MediaRecorder & { isTypeSupported?: (mime: string) => boolean })
        | null;
      if (MR && typeof MR.isTypeSupported === 'function' && MR.isTypeSupported(c.mime)) {
        return c;
      }
    } catch {
      // ignora e tenta próximo
    }
  }
  // Fallback: deixa o navegador escolher (string vazia) e usa extensão webm
  return { mime: '', ext: 'webm' };
};

/**
 * Formata segundos em mm:ss para exibição do timer de gravação.
 */
const formatRecordingTime = (totalSeconds: number): string => {
  const m = Math.floor(totalSeconds / 60).toString().padStart(2, '0');
  const s = Math.floor(totalSeconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
};

export const InputArea: React.FC<InputAreaProps> = ({
  onSend,
  onSendFile,
  primaryColor,
  disabled = false,
  placeholder = 'Digite sua mensagem...',
  onTypingStart,
  onTypingStop,
  enableFileUpload = false,
}) => {
  const [value, setValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  // Estados da gravação de áudio
  const [isRecording, setIsRecording] = useState(false);
  const [isStartingRecording, setIsStartingRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  // Refs da gravação
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingMimeRef = useRef<{ mime: string; ext: string }>({ mime: '', ext: 'webm' });
  const recordingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Flag que indica se o stop deve descartar (cancelar) ou enviar
  const shouldCancelRef = useRef(false);

  // Flag estática: define se o botão de mic deve sequer aparecer
  const audioSupported = isAudioRecordingSupported();

  // Valida e envia arquivo
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !onSendFile) return;

    // Validar tipo
    const isImage = ALLOWED_FILE_TYPES.image.includes(file.type);
    const isDocument = ALLOWED_FILE_TYPES.document.includes(file.type);
    const isAudio = ALLOWED_FILE_TYPES.audio.includes(file.type);

    if (!isImage && !isDocument && !isAudio) {
      alert('Tipo de arquivo não permitido. Use imagens (JPG, PNG, GIF, WebP), documentos (PDF, DOC, DOCX) ou áudios (MP3, WAV, OGG, M4A).');
      return;
    }

    // Validar tamanho
    if (file.size > MAX_FILE_SIZE) {
      alert('Arquivo muito grande. Tamanho máximo: 10MB.');
      return;
    }

    setIsUploading(true);
    try {
      await onSendFile(file);
    } finally {
      setIsUploading(false);
      // Limpa input para permitir selecionar o mesmo arquivo novamente
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Auto-resize do textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  }, [value]);

  // Gerencia estado de "digitando"
  useEffect(() => {
    if (value && onTypingStart) {
      onTypingStart();

      // Limpa timeout anterior
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      // Para de digitar após 2 segundos de inatividade
      typingTimeoutRef.current = setTimeout(() => {
        onTypingStop?.();
      }, 2000);
    }

    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [value, onTypingStart, onTypingStop]);

  // ============================================================
  // GRAVAÇÃO DE ÁUDIO
  // ============================================================

  /**
   * Libera todos os recursos da gravação: tracks do microfone,
   * timer do contador e referências internas. Idempotente.
   */
  const cleanupRecording = () => {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
    mediaRecorderRef.current = null;
    audioChunksRef.current = [];
  };

  /**
   * Inicia a gravação solicitando permissão do microfone só no clique.
   * Em caso de erro (permissão negada, sem mic) exibe mensagem clara.
   */
  const startRecording = async () => {
    if (isRecording || isStartingRecording || disabled || isUploading) return;

    setIsStartingRecording(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;

      const picked = pickBestAudioMime();
      recordingMimeRef.current = picked;

      // Algumas implementações lançam se passado MIME vazio; passar undefined nesse caso.
      const recorder = picked.mime
        ? new MediaRecorder(stream, { mimeType: picked.mime })
        : new MediaRecorder(stream);

      audioChunksRef.current = [];
      shouldCancelRef.current = false;

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = async () => {
        // Snapshot dos refs ANTES de cleanup
        const chunks = audioChunksRef.current.slice();
        const { mime, ext } = recordingMimeRef.current;
        const wasCancelled = shouldCancelRef.current;

        cleanupRecording();
        setIsRecording(false);
        setRecordingSeconds(0);

        if (wasCancelled || chunks.length === 0 || !onSendFile) {
          return;
        }

        // Monta o Blob final com o MIME real usado pelo recorder
        const blobMime = mime || chunks[0]?.type || 'audio/webm';
        const blob = new Blob(chunks, { type: blobMime });

        // Limite de tamanho – evita upload absurdo
        if (blob.size > MAX_FILE_SIZE) {
          alert('Áudio muito longo. Tamanho máximo: 10MB.');
          return;
        }

        const file = new File([blob], `audio-${Date.now()}.${ext}`, { type: blobMime });

        setIsUploading(true);
        try {
          await onSendFile(file);
        } finally {
          setIsUploading(false);
        }
      };

      recorder.onerror = () => {
        cleanupRecording();
        setIsRecording(false);
        setRecordingSeconds(0);
        alert('Ocorreu um erro durante a gravação do áudio.');
      };

      mediaRecorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);
      setRecordingSeconds(0);

      // Timer visual + auto-stop ao atingir limite máximo
      recordingTimerRef.current = setInterval(() => {
        setRecordingSeconds((prev) => {
          const next = prev + 1;
          if (next >= MAX_RECORDING_SECONDS) {
            // Auto-envia ao atingir limite
            stopRecording(false);
          }
          return next;
        });
      }, 1000);
    } catch (err: unknown) {
      cleanupRecording();
      setIsRecording(false);
      const name = (err as { name?: string } | null)?.name;
      if (name === 'NotAllowedError' || name === 'SecurityError') {
        alert('Permissão de microfone negada. Habilite o microfone nas configurações do navegador para enviar áudios.');
      } else if (name === 'NotFoundError') {
        alert('Nenhum microfone encontrado neste dispositivo.');
      } else {
        alert('Não foi possível iniciar a gravação de áudio.');
      }
    } finally {
      setIsStartingRecording(false);
    }
  };

  /**
   * Encerra a gravação. Se `cancel=true`, descarta os chunks (não envia).
   */
  const stopRecording = (cancel: boolean) => {
    const recorder = mediaRecorderRef.current;
    if (!recorder) {
      cleanupRecording();
      setIsRecording(false);
      setRecordingSeconds(0);
      return;
    }
    shouldCancelRef.current = cancel;
    try {
      if (recorder.state !== 'inactive') {
        recorder.stop(); // dispara onstop, que fará upload ou descarte
      } else {
        cleanupRecording();
        setIsRecording(false);
        setRecordingSeconds(0);
      }
    } catch {
      cleanupRecording();
      setIsRecording(false);
      setRecordingSeconds(0);
    }
  };

  // Cleanup ao desmontar – garante que o microfone é liberado mesmo se
  // o widget for fechado durante uma gravação ativa.
  useEffect(() => {
    return () => {
      cleanupRecording();
    };
  }, []);

  // Envia mensagem
  const handleSend = () => {
    if (value.trim() && !disabled) {
      onSend(value.trim());
      setValue('');
      onTypingStop?.();

      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
        textareaRef.current.focus();
      }
    }
  };

  // Envia com Enter
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const canSend = value.trim() && !disabled && !isUploading && !isRecording;
  // Bloqueia textarea e botões auxiliares enquanto grava ou faz upload
  const auxDisabled = disabled || isUploading || isRecording || isStartingRecording;

  // Ícone de anexo
  const AttachmentIcon = () => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
    </svg>
  );

  // Ícone do microfone
  const MicIcon = () => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
      <line x1="12" y1="19" x2="12" y2="23" />
      <line x1="8" y1="23" x2="16" y2="23" />
    </svg>
  );

  // Ícone de lixeira (cancelar gravação)
  const TrashIcon = () => (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
    </svg>
  );

  // Estilos do container - padding reduzido em 35%
  const containerStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '6px',
    padding: '8px',
    borderTop: '1px solid #e5e7eb',
    backgroundColor: '#ffffff',
    transition: 'box-shadow 0.2s ease',
    boxShadow: isFocused ? '0 -4px 12px rgba(0, 0, 0, 0.05)' : 'none',
  };

  // Estilos do wrapper do textarea
  const textareaWrapperStyles: React.CSSProperties = {
    flex: 1,
    position: 'relative',
    borderRadius: '24px',
    border: `2px solid ${isFocused ? primaryColor : '#e5e7eb'}`,
    backgroundColor: auxDisabled ? '#f9fafb' : '#ffffff',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    boxShadow: isFocused ? `0 0 0 3px ${primaryColor}15` : 'none',
  };

  // Estilos do textarea
  const textareaStyles: React.CSSProperties = {
    width: '100%',
    padding: '10px 16px',
    border: 'none',
    borderRadius: '20px',
    fontSize: '16px',
    lineHeight: '1.4',
    resize: 'none',
    outline: 'none',
    minHeight: '40px',
    maxHeight: '100px',
    fontFamily: 'inherit',
    backgroundColor: 'transparent',
    boxSizing: 'border-box',
    color: '#1f2937',
  };

  // Estilos do botão de enviar texto
  const buttonStyles: React.CSSProperties = {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: canSend ? primaryColor : '#e5e7eb',
    border: 'none',
    cursor: canSend ? 'pointer' : 'not-allowed',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
    flexShrink: 0,
    boxShadow: canSend ? `0 2px 8px ${primaryColor}40` : 'none',
    transform: canSend ? 'scale(1)' : 'scale(0.95)',
  };

  // Ícone de envio
  const SendIcon = ({ stroke = '#ffffff' }: { stroke?: string }) => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke={stroke}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ transition: 'transform 0.2s ease' }}
    >
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22,2 15,22 11,13 2,9" />
    </svg>
  );

  // Botões auxiliares (anexo/microfone) - mesmo tamanho
  const auxButtonStyles: React.CSSProperties = {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: auxDisabled ? 'not-allowed' : 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#9ca3af',
    transition: 'all 0.2s ease',
    flexShrink: 0,
    opacity: auxDisabled ? 0.5 : 1,
  };

  // Estilos do painel de gravação (substitui textarea enquanto grava)
  const recordingPanelStyles: React.CSSProperties = {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '8px 14px',
    borderRadius: '24px',
    border: `2px solid ${primaryColor}`,
    backgroundColor: '#fff5f5',
    minHeight: '44px',
  };

  const recordingDotStyles: React.CSSProperties = {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: '#ef4444',
    flexShrink: 0,
    animation: 'uaizychatPulse 1.2s ease-in-out infinite',
  };

  const recordingTimeStyles: React.CSSProperties = {
    fontSize: '14px',
    color: '#1f2937',
    fontVariantNumeric: 'tabular-nums',
    fontWeight: 500,
  };

  const recordingHintStyles: React.CSSProperties = {
    flex: 1,
    fontSize: '12px',
    color: '#6b7280',
  };

  return (
    <>
      <style>
        {`
          .uaizychat-send-btn:hover:not(:disabled) {
            transform: scale(1.08) !important;
            box-shadow: 0 4px 12px ${primaryColor}50 !important;
          }
          .uaizychat-send-btn:active:not(:disabled) {
            transform: scale(0.95) !important;
          }
          .uaizychat-aux-btn:hover:not(:disabled) {
            color: ${primaryColor} !important;
            background-color: #f3f4f6 !important;
          }
          .uaizychat-mic-btn-recording {
            color: #ef4444 !important;
            background-color: #fee2e2 !important;
          }
          .uaizychat-textarea::placeholder {
            color: #d1d5db;
            font-size: 13px;
          }
          .uaizychat-textarea:disabled::placeholder {
            color: #d1d5db;
          }
          .uaizychat-textarea {
            color: #1f2937 !important;
            -webkit-text-fill-color: #1f2937 !important;
          }
          @keyframes uaizychatPulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.4; transform: scale(0.85); }
          }
        `}
      </style>

      {/* Input de arquivo oculto - aceita imagens, documentos E áudios */}
      <input
        ref={fileInputRef}
        type="file"
        accept={[
          ...ALLOWED_FILE_TYPES.image,
          ...ALLOWED_FILE_TYPES.document,
          ...ALLOWED_FILE_TYPES.audio,
        ].join(',')}
        onChange={handleFileSelect}
        style={{ display: 'none' }}
        disabled={auxDisabled}
      />

      <div style={containerStyles}>
        {/* Botão de anexo - escondido durante gravação para liberar espaço */}
        {enableFileUpload && onSendFile && !isRecording && (
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={auxDisabled}
            style={auxButtonStyles}
            className="uaizychat-aux-btn"
            aria-label="Anexar arquivo"
            title="Anexar arquivo"
          >
            {isUploading ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" strokeDasharray="60" strokeDashoffset="20">
                  <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite"/>
                </circle>
              </svg>
            ) : (
              <AttachmentIcon />
            )}
          </button>
        )}

        {/* Painel de gravação OU textarea normal */}
        {isRecording ? (
          <div style={recordingPanelStyles} role="status" aria-live="polite">
            <span style={recordingDotStyles} aria-hidden="true" />
            <span style={recordingTimeStyles}>{formatRecordingTime(recordingSeconds)}</span>
            <span style={recordingHintStyles}>Gravando áudio…</span>
          </div>
        ) : (
          <div style={textareaWrapperStyles}>
            <textarea
              ref={textareaRef}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              placeholder={isUploading ? 'Enviando arquivo...' : placeholder}
              disabled={auxDisabled}
              style={textareaStyles}
              className="uaizychat-textarea"
              rows={1}
              aria-label="Digite sua mensagem"
            />
          </div>
        )}

        {/* Botão de cancelar (só durante gravação) */}
        {isRecording && (
          <button
            onClick={() => stopRecording(true)}
            style={auxButtonStyles}
            className="uaizychat-aux-btn"
            aria-label="Cancelar gravação"
            title="Cancelar gravação"
          >
            <TrashIcon />
          </button>
        )}

        {/* Botão de microfone - só aparece se suportado e quando há texto não há texto digitado */}
        {audioSupported && onSendFile && enableFileUpload && !value.trim() && (
          <button
            onClick={isRecording ? () => stopRecording(false) : startRecording}
            disabled={disabled || isUploading || isStartingRecording}
            style={{
              ...auxButtonStyles,
              ...(isRecording
                ? { color: '#ef4444', backgroundColor: '#fee2e2' }
                : {}),
            }}
            className={`uaizychat-aux-btn ${isRecording ? 'uaizychat-mic-btn-recording' : ''}`}
            aria-label={isRecording ? 'Enviar áudio gravado' : 'Gravar áudio'}
            title={isRecording ? 'Enviar áudio' : 'Gravar áudio'}
          >
            {isRecording ? <SendIcon stroke="#ef4444" /> : <MicIcon />}
          </button>
        )}

        {/* Botão de enviar texto - escondido durante gravação (já há botão de envio próprio) */}
        {!isRecording && value.trim().length > 0 && (
          <button
            onClick={handleSend}
            disabled={!canSend}
            style={buttonStyles}
            className="uaizychat-send-btn"
            aria-label="Enviar mensagem"
          >
            <SendIcon />
          </button>
        )}

        {/* Quando NÃO está gravando E NÃO há texto E NÃO há suporte a áudio,
            ainda exibimos o botão de enviar (desabilitado) para manter layout consistente */}
        {!isRecording && !value.trim() && (!audioSupported || !onSendFile || !enableFileUpload) && (
          <button
            onClick={handleSend}
            disabled
            style={buttonStyles}
            className="uaizychat-send-btn"
            aria-label="Enviar mensagem"
          >
            <SendIcon />
          </button>
        )}
      </div>
    </>
  );
};
